import { InputField } from '@/components/form/InputField';
import PasswordField from '@/components/form/PasswordField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { withAuthGuard } from '@/hoc/withAuthGuard';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { setTaraReturnPath } from '@/utils/auth';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { createLoginSchema, type LoginValues } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Spinner,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import {
  createPath,
  type Path,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { toast } from 'react-toastify';

interface LoginProps {}

const LoginComponent: FC<LoginProps> = () => {
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [isPending, setIsPending] = useState(false);
  const { loginWithPassword, startTaraLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const enableTaraLogin = !!useTenantConfiguration()?.enableTaraLogin;

  usePageTitle('logIn');

  const form = useForm<LoginValues>({
    mode: 'onBlur',
    resolver: zodResolver(createLoginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = useCallback(
    async (values: LoginValues) => {
      setIsPending(true);
      try {
        await loginWithPassword(values);
        navigate('/purchase', { replace: true });
      } catch (error) {
        toast.error(
          <Typography variant="body1">
            <TransText i18nKey="loginFailed" />
            {error instanceof Error ? `: ${error.message}` : ''}
          </Typography>
        );
      } finally {
        setIsPending(false);
      }
    },
    [loginWithPassword, navigate]
  );

  const handleTaraLogin = useCallback(() => {
    const from = (location.state as { from?: Partial<Path> } | null)?.from;

    setTaraReturnPath(from ? createPath(from) : null);
    startTaraLogin();
  }, [location.state, startTaraLogin]);

  const hasHandedOff = useRef(false);

  useEffect(() => {
    if (!enableTaraLogin || hasHandedOff.current) {
      return;
    }

    hasHandedOff.current = true;
    handleTaraLogin();
  }, [enableTaraLogin, handleTaraLogin]);

  if (enableTaraLogin) {
    return (
      <CenteredLayout>
        <div className="mx-auto flex w-full flex-col items-center gap-4 p-4">
          <Spinner size={40} />
          <Typography variant="body1">
            <TransText i18nKey="signingYouIn" />
          </Typography>
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
        <Typography variant={isLaptopOrBigger ? 'title' : 'mobile-title'}>
          <TransText i18nKey="logIn" />
        </Typography>
        <Card className="flex flex-col">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn('flex flex-col gap-3 [&_input]:h-12', {
              '[&_input]:h-8': isLaptopOrBigger,
            })}
          >
            <InputField
              name="email"
              required
              placeholder={t('text.enterYourEmailAddress')}
              label={<TransText i18nKey="email" />}
            />
            <PasswordField
              name="password"
              required
              placeholder={t('text.enterYourPassword')}
              label={<TransText i18nKey="password" />}
            />
            <Button
              data-testid="login-submit-button"
              type="submit"
              className="mt-3"
              size="medium"
              isPending={isPending}
            >
              <TransText i18nKey="logIn" />
            </Button>
          </Form>
          <div className="mt-4 flex flex-col items-center">
            <Button
              data-testid="forgot-password-button"
              onClick={() => navigate('/reset-password')}
              variant="ghost"
              className="self-center"
            >
              <TransText i18nKey="forgotPassword" />
              &#63;
            </Button>
            <div className="flex items-center gap-3 self-center">
              <Typography variant="body1">
                <TransText i18nKey="notRegisteredYet" />
              </Typography>
              <Button
                variant="ghost"
                data-testid="create-an-account-button"
                className={cn('w-fit p-0', 'text-primary')}
                onClick={() => navigate('/signup')}
              >
                <TransText i18nKey="createAnAccount" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </CenteredLayout>
  );
};

const Login = withAuthGuard('loginOrSignup')(LoginComponent);

export default Login;
